import React, { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useRoleContext, RoleContextType } from '../context/RoleContext';
import { useServiceKey } from '../hooks/useServiceKey';
import ProgressDialog from './ProgressDialog';

interface ProtectedRouteProps {
  children: ReactNode;
  allowedRoles: Array<'superadmin' | 'serviceprovideradmin' | 'customer'>;
}

/**
 * ProtectedRoute component checks user role against allowedRoles.
 * If the role is not allowed or user is not authenticated, it redirects to the service key's default path.
 * If role is still loading, it shows a loading indicator.
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, allowedRoles }) => {
  const { userProfile, loading: roleLoading }: RoleContextType = useRoleContext();
  const { serviceKey, isLoading: serviceKeyLoading, isValidKey } = useServiceKey(); // Added isValidKey
  const location = useLocation();

  if (roleLoading || serviceKeyLoading) {
    return (
      <ProgressDialog isOpen={true} message="Loading access control..." subMessage="Please wait while we verify your permissions." />
    );
  }

  const isAuthenticated = !!userProfile;
  const userRole = userProfile?.role;

  // Determine the fallback path: `/[servicekey]` if valid and serviceKey exists, else `/`.
  // This path is used for redirects when access is denied (e.g., user not authenticated or role not allowed).
  const fallbackPath = isValidKey && serviceKey ? `/${serviceKey}` : '/';

  if (!isAuthenticated) {
    // Redirect to login page if not authenticated
    // Storing the intended location to redirect back after login
    const fullPath = location.pathname + location.search;
    const loginRedirect = `/login?redirect=${encodeURIComponent(fullPath)}`;
    console.warn(`[ProtectedRoute] User not authenticated. Redirecting to login: ${loginRedirect} from ${location.pathname}${location.search}`);
    // Store intended path in sessionStorage for extra reliability
    try {
      sessionStorage.setItem('postLoginRedirect', fullPath);
      console.log(`[ProtectedRoute] Stored postLoginRedirect in sessionStorage: ${fullPath}`);
    } catch (e) {
      console.error('[ProtectedRoute] Failed to store postLoginRedirect in sessionStorage', e);
    }
    // Extra debug: log Navigate state and props
    console.debug('[ProtectedRoute] Navigate to login', {
      loginRedirect,
      location,
      state: { from: location },
      allowedRoles,
      isAuthenticated,
      userProfile,
      serviceKey,
      isValidKey,
      serviceKeyLoading: serviceKeyLoading,
      roleLoading: roleLoading
    });
    return <Navigate to={loginRedirect} state={{ from: location }} replace />;
  }

  if (!userRole || !allowedRoles.includes(userRole)) {
    // Redirect to service key default path or root if role is not allowed
    console.warn(`[ProtectedRoute] Access denied for role '${userRole}' to path '${location.pathname}'. Allowed: ${allowedRoles.join(', ')}. Redirecting to ${fallbackPath}`);
    return <Navigate to={fallbackPath} replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
